import { MorphemeTileGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.glyph-renderer";
import {
  MorphemeTilePresentationProvider,
  useMorphemeTilePresentation,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.tile.ui.presentation";
import type { MorphemeDetailsTileSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { View } from "@-/com.npmjs.react-native";
import runes from "@-/com.npmjs.runes2";
import { stringLength } from "@-/com.npmjs.string-length";
import { GraphemeFontGlyph } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.grapheme.glyph.font";
import {
  createWordGlyph,
  WordFontGlyph,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.word.glyph.font";
import { pinyinForGrapheme } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.glyph";
import { splitToneNumbersPinyinSyllables } from "@-/dev.improve.haniverse.lib.morpheme.grapheme.pinyin";
import { toneForPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.transcription";
import { useMemo } from "react";
import {
  getMorphemeDetailsTileGlyphStyle,
  getMorphemeDetailsTileMultiCharacterGlyphStyle,
} from "#morpheme-details-tile-morpheme-sizing";
import arrayLength from "@-/com.npmjs.extra-array__length";

function toneColorForPinyin({
  isToneColorsEnabled,
  pinyin,
  toneColors,
}: {
  isToneColorsEnabled: boolean;
  pinyin: string | null | undefined;
  toneColors: Record<number, string>;
}): string | null {
  if (!isToneColorsEnabled || pinyin == null || pinyin.length === 0) {
    return null;
  }
  const tone = toneForPinyin(pinyin);
  return tone === null ? null : toneColors[tone];
}

function MorphemeDetailsTileGlyphContent({
  align = "start",
  availableHeight,
  availableWidth = null,
  graphemeCard,
  primaryToneNumbersPinyin = null,
  readingToneNumbersPinyin = null,
}: {
  /** Horizontal placement of the glyph within its box. Defaults to `"start"`
   * (the morpheme grid's top-left look); `"center"` is used by the card tile. */
  align?: "start" | "center";
  availableHeight: number;
  availableWidth?: number | null;
  graphemeCard: MorphemeDetailsTileSlice;
  primaryToneNumbersPinyin?: string | null;
  /**
   * The reading a single character is tone-coloured by (a pronunciation card's own), instead of its
   * main reading.
   */
  readingToneNumbersPinyin?: string | null;
}) {
  const { getGraphemeGlyph, isToneColorsEnabled, svgTileGlyphsEnabled, toneColors, user } =
    useMorphemeTilePresentation();
  const isMultiCharacter = stringLength(graphemeCard.front) > 1;
  const readingColor = isMultiCharacter
    ? null
    : toneColorForPinyin({ isToneColorsEnabled, pinyin: readingToneNumbersPinyin, toneColors });
  const singleCharacterGlyph = getGraphemeGlyph(
    graphemeCard.front,
    readingColor === null ? undefined : { color: readingColor },
  );
  const characterCount = arrayLength(runes(graphemeCard.front));
  const glyphStyle =
    isMultiCharacter && availableWidth != null
      ? getMorphemeDetailsTileMultiCharacterGlyphStyle({
          availableHeight,
          availableWidth,
          characterCount,
        })
      : getMorphemeDetailsTileGlyphStyle({ availableHeight });
  const wordGlyphList = useMemo(() => {
    if (!isMultiCharacter) {
      return null;
    }
    const characters = runes(graphemeCard.front);
    const syllables =
      primaryToneNumbersPinyin == null
        ? []
        : splitToneNumbersPinyinSyllables(primaryToneNumbersPinyin);
    return createWordGlyph(
      characters.map((character, index) => {
        const syllable = syllables[index];
        const fallbackPinyin = pinyinForGrapheme(character);
        const color = toneColorForPinyin({
          isToneColorsEnabled,
          pinyin: syllable ?? fallbackPinyin,
          toneColors,
        });
        return getGraphemeGlyph(character, { color });
      }),
    );
  }, [
    graphemeCard.front,
    getGraphemeGlyph,
    isMultiCharacter,
    isToneColorsEnabled,
    primaryToneNumbersPinyin,
    toneColors,
  ]);

  const alignClassName = align === "center" ? "items-center justify-center" : "";
  const glyphTextStyle =
    align === "center" ? [glyphStyle, { textAlign: "center" as const }] : glyphStyle;

  return (
    <View className={`min-h-0 min-w-0 flex-1 self-stretch ${alignClassName}`}>
      {isMultiCharacter && wordGlyphList != null ? (
        svgTileGlyphsEnabled ? (
          <View className="flex-row items-center">
            {wordGlyphList.map((glyph, index) => (
              <MorphemeTileGlyph
                fallback={
                  <GraphemeFontGlyph
                    className="text-foreground"
                    glyph={glyph}
                    hanziScript={user.hanziScript}
                    style={glyphTextStyle}
                  />
                }
                glyph={glyph}
                key={`${glyph.original}-${index}`}
                size={glyphStyle.fontSize}
              />
            ))}
          </View>
        ) : (
          <WordFontGlyph
            className="shrink-0 text-foreground"
            glyphList={wordGlyphList}
            hanziScript={user.hanziScript}
            style={glyphTextStyle}
          />
        )
      ) : (
        <MorphemeTileGlyph
          fallback={
            <GraphemeFontGlyph
              className="shrink-0 text-foreground"
              glyph={singleCharacterGlyph}
              hanziScript={user.hanziScript}
              style={glyphTextStyle}
            />
          }
          glyph={singleCharacterGlyph}
          size={glyphStyle.fontSize}
        />
      )}
    </View>
  );
}

export function MorphemeDetailsTileGlyph(
  props: React.ComponentProps<typeof MorphemeDetailsTileGlyphContent>,
) {
  return (
    <MorphemeTilePresentationProvider>
      <MorphemeDetailsTileGlyphContent {...props} />
    </MorphemeTilePresentationProvider>
  );
}
